import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext } from 'core/states';
import { useJobVendorActions } from 'jobs/hooks';
import { IJobVendor } from 'jobs/types';
import { Color, Variant } from 'theme/enums';

import { RemoveVendorsConfirmation } from './RemoveVendorsConfirmation';

interface IProps {
  jobId: number;
  vendors: IJobVendor[];
  buttonElement?: ReactElement;
}

export const RemoveVendorsButton = ({ vendors, buttonElement }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { updateRows, deselectAllRows } = useAgGridClientTableContext();
  const { removeJobVendors, isLoading } = useJobVendorActions(NotificationPosition.TOP);

  const onRemove = async () => {
    const vendorIds = vendors.filter((vendor) => vendor.id).map((vendor) => vendor.id);

    const { success } = await removeJobVendors(vendorIds);

    if (success) {
      deselectAllRows();
      updateRows({ remove: vendors });
    }

    setIsConfirmationOpen(false);
  };

  const onClick = () => {
    setIsConfirmationOpen(true);
  };

  if (buttonElement) {
    return cloneElement(buttonElement, {
      onClick: onClick,
      disabled: !vendors?.length || isLoading,
    });
  }

  return (
    <>
      <Button
        text={t('buttonLabelRemove')}
        variant={Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<Trash />}
        onClick={onClick}
        disabled={!vendors?.length || isLoading}
      />

      <RemoveVendorsConfirmation
        isOpen={isConfirmationOpen}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
        vendors={vendors}
      />
    </>
  );
};
